import { createApp } from "vue";
import { createI18n } from "vue-i18n";
import { BarChart } from "echarts/charts";
import VChart, { THEME_KEY } from "vue-echarts";
import { CanvasRenderer } from 'echarts/renderers';
import { faArrowRightArrowLeft, faBan, faBolt, faCameraRetro, faCaretDown, faCaretUp, faChartColumn, faChartLine, faChartPie, faCheck, faCircleArrowRight, faCircleCheck, faCircleInfo, faCircleXmark, faClipboardQuestion, faClock, faComment, faEnvelope, faExclamationCircle, faFaceLaugh, faFileCircleCheck, faFilePen, faFloppyDisk, faGears, faHourglassHalf, faInbox, faKey, faKeyboard, faLanguage, faList, faMagnifyingGlassMinus, faMask, faMessage, faNetworkWired, faPaperclip, faPersonCircleCheck, faPersonCircleExclamation, faQuestion, faRotate, faRotateRight, faSolarPanel, faSquarePlus, faSquareXmark, faTableList, faTags, faTriangleExclamation, faUser, faUserClock, faUserSecret, faUserShield, faUserSlash, faUserTag, faUsers, faWandMagicSparkles, faXmark } from "@fortawesome/free-solid-svg-icons";
import { faAppStoreIos, faFacebook, faFacebookF, faGooglePlay, faInstagram, faLinkedin, faLinkedinIn, faSquareInstagram, faTiktok, faXTwitter, faYoutube } from "@fortawesome/free-brands-svg-icons";
import { faFaceAngry, faFaceLaugh as faFaceLaughRegular, faFaceSadTear, faFolderOpen, faNoteSticky, faRectangleList, faSquarePlus as faSquarePlusRegular } from "@fortawesome/free-regular-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { GridComponent, TooltipComponent, LegendComponent } from "echarts/components";
import { registerI18n } from "./configs/localization/localeLoader";

import App from "./App.vue";
import router from "./router";
import store from "./store/index";
import utils from "./utils/utils";
import Vue3TagsInput from "vue3-tags-input";
import Select2 from "vue3-select2-component";
import VueAwesomePaginate from "vue-awesome-paginate";
import PlainCard from "./components/ui/PlainCard.vue";
import "@fred78290/vue3-content-placeholders/style.css";
// Kütüphane stilleri global yüklenir: bileşenler bunları her yerde varsayıyor ve uygulamanın kendi stilleri bunların üstüne yazıyor.
// Bileşen içinden import edilirse yalnızca o sayfanın chunk'ıyla ve uygulama stillerinden sonra iner.
import "datatables.net-dt/css/dataTables.dataTables.css";
import "vue-datepicker-next/index.css";
import "vue-multiselect/dist/vue-multiselect.min.css";
import "vue3-simple-typeahead/dist/vue3-simple-typeahead.css";
import "vue-awesome-paginate/dist/style.css";
import ButtonBase from "./components/ui/ButtonBase.vue";
import DialogBase from "./components/ui/DialogBase.vue";
import PageLoading from "./components/ui/PageLoading.vue";
import SwitchCheckbox from "./components/ui/SwitchCheckbox.vue";
import NotificationBase from "./components/ui/NotificationBase.vue";
import VueContentPlaceholders from "@fred78290/vue3-content-placeholders";

// Dil kaynakları: geliştirmede (npm run serve) kaynak dosyalardan doğrudan, tüm diller birden yüklenir; çeviri değişikliği anında görünür.
// Diğer modlarda scripts/build-locales.js'in ürettiği dil başına dosyalar kullanılır: ilk pakette yalnızca yedek dil (tr) bulunur,
// kullanıcının dili localeLoader ile gerektiğinde indirilir. Kaynak dosyaların listesi ve birleştirme sırası locale-sources.json'dadır.
let mergedMessages;

if (process.env.NODE_ENV === "development") {
	const localeSources = require("./configs/localization/locale-sources.json");
	const localizationContext = require.context("./configs/localization", true, /\.js$/);

	mergedMessages = {};

	localeSources.languages.forEach(lang => {
		mergedMessages[lang] = Object.assign({}, ...localeSources.sources.map(({ file, export: exportName }) => localizationContext(`./${file}.js`)[exportName][lang] || {}));
	});
} else {
	mergedMessages = { tr: require("./configs/localization/generated/tr.json") };
}

const activeLocale = store.getters.languageId;

const mergedResources = createI18n({
	locale: activeLocale,
	fallbackLocale: "tr",
	messages: mergedMessages
});

registerI18n(mergedResources);

const purifyDirective = {
    beforeMount(el, binding) {
        el.innerText = utils.utility.purify(binding.value);
    },
    updated(el, binding) {
        el.innerText = utils.utility.purify(binding.value);
    }
};

library.add(faArrowRightArrowLeft, faBan, faBolt, faCameraRetro, faCaretDown, faCaretUp, faChartColumn, faChartLine, faChartPie, faCheck, faCircleArrowRight, faCircleCheck, faCircleInfo, faCircleXmark, faClipboardQuestion, faClock, faComment, faEnvelope, faExclamationCircle, faFaceLaugh, faFileCircleCheck, faFilePen, faFloppyDisk, faGears, faHourglassHalf, faInbox, faKey, faKeyboard, faLanguage, faList, faMagnifyingGlassMinus, faMask, faMessage, faNetworkWired, faPaperclip, faPersonCircleCheck, faPersonCircleExclamation, faQuestion, faRotate, faRotateRight, faSolarPanel, faSquarePlus, faSquareXmark, faTableList, faTags, faTriangleExclamation, faUser, faUserClock, faUserSecret, faUserShield, faUserSlash, faUserTag, faUsers, faWandMagicSparkles, faXmark, faAppStoreIos, faFacebook, faFacebookF, faGooglePlay, faInstagram, faLinkedin, faLinkedinIn, faSquareInstagram, faTiktok, faXTwitter, faYoutube, faFaceAngry, faFaceLaughRegular, faFaceSadTear, faFolderOpen, faNoteSticky, faRectangleList, faSquarePlusRegular);

const app = createApp(App);

app.use(store);
app.use(router);
app.use(mergedResources);
app.use(VueAwesomePaginate);
app.use(VueContentPlaceholders);
app.use([GridComponent, BarChart, CanvasRenderer, TooltipComponent, LegendComponent]);

app.provide(THEME_KEY, 'light');

app.component("v-chart", VChart);
app.component("select-2", Select2);
app.component("plain-card", PlainCard);
app.component("dialog-base", DialogBase);
app.component("button-base", ButtonBase);
app.component("page-loading", PageLoading);
app.component("vue3-tags-input", Vue3TagsInput);
app.component("switch-checkbox", SwitchCheckbox);
app.component("font-awesome-icon", FontAwesomeIcon);
app.component("notification-base", NotificationBase);

app.directive("purify", purifyDirective);

app.config.errorHandler = (error) => {
	if (error && error.cause != 403 && error.cause != 404) {
        const isDialogOpened = store.getters.dialogOpened;

        if (isDialogOpened) {
            store.dispatch("throwFormError", error);
        } else {
            store.dispatch("throwError", error);
        }
    }
};

app.mount("#app");